import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { getTodayString } from "../utils/timezone";
import "./TodoList.css";
import TodoAssistant from "./TodoAssistant";
import calendarService from "../services/calendarService";
import todoService from "../services/todoService";

const TodoList = ({
  todos,
  onAddTodo,
  onToggleTodo,
  onDeleteTodo,
  onMarkTodoMissed,
  onRescheduleTodo,
  onClearMissed,
  onDeleteAll,
  onClearCompleted,
  onEditTodo,
  onTodoCalendarChange,
  isAddingTodo = false,
  togglingTodo = {},
  deletingTodo = {},
  onConnectCalendar,
}) => {
  const [newTodo, setNewTodo] = useState("");
  const [deadline, setDeadline] = useState("");
  const [reminderMinutes, setReminderMinutes] = useState(0);
  const [reminderTime, setReminderTime] = useState("09:00");
  const [showNewTodoCal, setShowNewTodoCal] = useState(false);
  const [newTodoCalForm, setNewTodoCalForm] = useState({ date: "", time: "09:00", reminderMinutes: 0 });
  const [calendarStatuses, setCalendarStatuses] = useState({});
  const [showPickerForTodo, setShowPickerForTodo] = useState(null);
  const [calendarForm, setCalendarForm] = useState({ date: "", time: "14:00", reminderMinutes: 15 });
  // Missed handling: bottom tray + re-remind prompt
  const [showMissed, setShowMissed] = useState(false); // collapsed by default so missed never overshadows to-dos
  const [missedPrompt, setMissedPrompt] = useState(null); // todo object awaiting re-remind choice
  const [remindDate, setRemindDate] = useState("");

  // Drag-and-drop state
  const [orderedTodos, setOrderedTodos] = useState(null); // null = use prop sort
  const [dragOverId, setDragOverId] = useState(null);
  const [dragAbove, setDragAbove] = useState(true);
  const dragIdRef = useRef(null);
  const reorderTimerRef = useRef(null);
  const prevTodosLenRef = useRef(todos.length);

  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const editInputRef = useRef(null);

  // Reset local drag order when todos are added/deleted, and patch
  // completed/text changes so the dragged view doesn't go stale
  const prevTodosSigRef = useRef("");
  const todosSig = todos.map((t) => `${t._id}:${t.completed ? 1 : 0}:${t.text}:${t.sortOrder ?? 0}`).join("|");
  if (todosSig !== prevTodosSigRef.current) {
    prevTodosSigRef.current = todosSig;
    if (orderedTodos !== null) {
      const byId = new Map(todos.map((t) => [t._id, t]));
      // Drop deleted, patch updated fields, keep drag order for the rest
      const patched = orderedTodos
        .filter((t) => byId.has(t._id))
        .map((t) => ({ ...t, ...byId.get(t._id), sortOrder: t.sortOrder }));
      // Append newly added todos
      const known = new Set(patched.map((t) => t._id));
      todos.forEach((t) => {
        if (!known.has(t._id)) patched.push(t);
      });
      if (patched.length !== orderedTodos.length || JSON.stringify(patched) !== JSON.stringify(orderedTodos)) {
        setOrderedTodos(patched.length === todos.length ? patched : null);
      }
    }
    prevTodosLenRef.current = todos.length;
  }

  useEffect(() => () => clearTimeout(reorderTimerRef.current), []);

  // Compute today string for overdue comparison (YYYY-MM-DD) - use local timezone
  const todayStr = getTodayString();

  // Build display list: prefer user-dragged order, else sort by sortOrder or date.
  // Missed always sink to the bottom (above completed) so a pile of missed
  // items never overshadows the need-to-do ones.
  const sortedTodos = orderedTodos ?? [...todos].sort((a, b) => {
    if (a.completed !== b.completed) return a.completed ? 1 : -1;
    if (a.completed && b.completed) return 0;
    if (!!a.missed !== !!b.missed) return a.missed ? 1 : -1;
    const anyHasOrder = todos.some(t => t.sortOrder && t.sortOrder !== 0);
    if (anyHasOrder) return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    if (!a.completed && !b.completed && !a.missed && !b.missed) {
      const aDate = a.deadline || todayStr;
      const bDate = b.deadline || todayStr;
      const aIsToday = aDate === todayStr, bIsToday = bDate === todayStr;
      const aIsOverdue = aDate < todayStr, bIsOverdue = bDate < todayStr;
      if (aIsToday && !bIsToday) return -1;
      if (!aIsToday && bIsToday) return 1;
      if (aIsOverdue && bIsOverdue) return aDate < bDate ? -1 : 1;
      if (aIsOverdue) return -1;
      if (bIsOverdue) return 1;
      return aDate < bDate ? -1 : 1;
    }
    return 0;
  });

  const activeTodos = sortedTodos.filter((t) => !t.completed && !t.missed);
  const missedTodos = sortedTodos.filter((t) => !t.completed && t.missed);
  const completedTodos = sortedTodos.filter((t) => t.completed);

  const getTomorrowStr = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const handleMissedClick = async (todo) => {
    if (todo.missed) {
      // Undo missed
      if (onMarkTodoMissed) await onMarkTodoMissed(todo._id, false);
      return;
    }
    if (onMarkTodoMissed) await onMarkTodoMissed(todo._id, true);
    setRemindDate(getTomorrowStr());
    setMissedPrompt(todo);
  };

  const handleRemindConfirm = async (mode) => {
    const target = missedPrompt;
    if (!target) return;
    try {
      const dateToUse = mode === "tomorrow" ? getTomorrowStr() : (remindDate || getTomorrowStr());
      if (typeof onRescheduleTodo === "function") {
        await onRescheduleTodo(target._id, dateToUse);
      } else {
        await todoService.rescheduleTodo(target._id, dateToUse);
      }
      toast.success(`🔔 Will remind on ${dateToUse}`);
    } catch {
      toast.error("Couldn't set re-reminder — try again");
    } finally {
      setMissedPrompt(null);
    }
  };

  const handleMissedDismiss = () => {
    // "No re-remind" — just keep it sunk at the bottom tray (collapsed).
    // It no longer clutters the need-to-do list above.
    setMissedPrompt(null);
    toast.info("Kept in Missed tray below — clear it anytime");
  };

  const handleMissedRemove = async () => {
    const target = missedPrompt;
    if (!target) return;
    setMissedPrompt(null);
    if (onDeleteTodo) await onDeleteTodo(target._id);
  };

  // DnD handlers
  const handleDragStart = useCallback((e, id) => {
    dragIdRef.current = id;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
    setTimeout(() => { const el = document.querySelector(`[data-todoid='${id}']`); if (el) el.classList.add("dragging"); }, 0);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragIdRef.current) {
      const el = document.querySelector(`[data-todoid='${dragIdRef.current}']`);
      if (el) el.classList.remove("dragging");
    }
    dragIdRef.current = null;
    setDragOverId(null);
  }, []);

  const handleDragOver = useCallback((e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    setDragAbove(e.clientY < rect.top + rect.height / 2);
    setDragOverId(id);
  }, []);

  const handleDrop = useCallback((e, targetId) => {
    e.preventDefault();
    const sourceId = dragIdRef.current;
    if (!sourceId || sourceId === targetId) { setDragOverId(null); return; }
    const base = orderedTodos ?? sortedTodos;
    const from = base.findIndex(t => t._id === sourceId);
    let to = base.findIndex(t => t._id === targetId);
    if (from === -1 || to === -1) { setDragOverId(null); return; }
    const reordered = [...base];
    const [moved] = reordered.splice(from, 1);
    // recalculate `to` after splice
    to = reordered.findIndex(t => t._id === targetId);
    const insertAt = dragAbove ? to : to + 1;
    reordered.splice(Math.max(0, insertAt), 0, moved);
    const withOrder = reordered.map((t, i) => ({ ...t, sortOrder: i + 1 }));
    setOrderedTodos(withOrder);
    setDragOverId(null);
    clearTimeout(reorderTimerRef.current);
    reorderTimerRef.current = setTimeout(() => {
      todoService.reorderTodos(withOrder.map(t => ({ id: t._id, sortOrder: t.sortOrder })))
        .catch(() => toast.error("Couldn't save order"));
    }, 600);
  }, [orderedTodos, sortedTodos, dragAbove]);

  // Inline edit handlers
  const handleEditStart = useCallback((todo) => {
    setEditingId(todo._id);
    setEditText(todo.text);
    setTimeout(() => editInputRef.current?.focus(), 0);
  }, []);

  const handleEditSave = useCallback(async (todo) => {
    const trimmed = editText.trim();
    if (!trimmed || trimmed === todo.text) { setEditingId(null); return; }
    setEditingId(null);
    // Prefer the parent updater (keeps server + list in sync). Fall back to
    // the local drag-order patch when no parent handler is wired.
    if (typeof onEditTodo === "function") {
      try {
        await onEditTodo(todo._id, trimmed);
      } catch {
        toast.error("Couldn't save edit — try again");
      }
      return;
    }
    try {
      await todoService.updateTodo(todo._id, { text: trimmed });
      // Patch text in local drag order so it doesn't revert
      setOrderedTodos(prev =>
        prev ? prev.map(t => t._id === todo._id ? { ...t, text: trimmed } : t) : null
      );
    } catch {
      toast.error("Couldn't save edit — try again");
    }
  }, [editText, onEditTodo]);

  const handleEditKeyDown = useCallback((e, todo) => {
    if (e.key === "Enter") { e.preventDefault(); handleEditSave(todo); }
    if (e.key === "Escape") { setEditingId(null); }
  }, [handleEditSave]);

  // Close picker with Escape
  React.useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setShowPickerForTodo(null);
      }
    };

    if (showPickerForTodo) {
      document.addEventListener("keydown", handleEsc);
    }
    return () => document.removeEventListener("keydown", handleEsc);
  }, [showPickerForTodo]);

  const handleCalBtnClick = (todo) => {
    const todoId = todo._id;
    if (showPickerForTodo === todoId) {
      setShowPickerForTodo(null);
      return;
    }

    const now = new Date();
    const roundedMinutes = Math.ceil(now.getMinutes() / 15) * 15;
    const defaultTime = todo.deadline
      ? "14:00"
      : `${String((roundedMinutes === 60 ? now.getHours() + 1 : now.getHours()) % 24).padStart(2, "0")}:${String(roundedMinutes % 60).padStart(2, "0")}`;

    setCalendarForm({
      date: todo.deadline || todayStr,
      time: defaultTime,
      reminderMinutes: 15,
    });

    setShowPickerForTodo(todoId);
  };

  // Warn when the reminder datetime is already in the past — Google never
  // fires notifications for past events, which looked like "reminder not working".
  const isReminderInPast = (dateStr, timeStr) => {
    if (!/^\d{2}:\d{2}$/.test(timeStr || "")) return false;
    const eventDate = new Date(`${dateStr}T${timeStr}:00`);
    if (Number.isNaN(eventDate.getTime())) return false;
    return eventDate.getTime() < Date.now() - 60000;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newTodo.trim()) return;
    const todoText = newTodo.trim();
    // If the reminder popup is still open (e.g. user hit Enter instead of
    // the popup's Add button), commit its values now instead of dropping them.
    const popupOpen = showNewTodoCal;
    const todoDeadline = (popupOpen ? (newTodoCalForm.date || todayStr) : null) || deadline || todayStr;
    // Capture reminder details before clearing state
    const capturedReminderMinutes = popupOpen ? Number(newTodoCalForm.reminderMinutes || 0) : reminderMinutes;
    const capturedReminderTime = popupOpen ? (newTodoCalForm.time || "09:00") : reminderTime;
    setNewTodo("");
    setDeadline("");
    setReminderMinutes(0);
    setReminderTime("09:00");
    setShowNewTodoCal(false);
    // Create the todo
    const created = await onAddTodo(todoText, todoDeadline);
    // If a reminder was requested and a todo was returned, add to calendar
    if (capturedReminderMinutes > 0 && created && created._id) {
      if (isReminderInPast(todoDeadline, capturedReminderTime || "09:00")) {
        toast.warn("⚠️ That reminder time is already past — Google won't notify for past events. Pick a future time.");
        return;
      }
      try {
        const result = await calendarService.smartAddToCalendar(
          {
            title: `✓ ${todoText}`,
            description: `Quick todo reminder`,
            date: todoDeadline,
            startTime: capturedReminderTime || "09:00",
            durationMinutes: 30,
            reminderMinutes: capturedReminderMinutes,
            todoId: created._id,
          },
          () => {
            // Not connected — show toast with connect prompt
            if (onConnectCalendar) {
              toast.info(
                ({ closeToast }) => (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span>Connect Google Calendar to save reminders</span>
                    <button
                      onClick={() => { closeToast(); onConnectCalendar(); }}
                      style={{
                        background: "#4285F4",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "4px 12px",
                        cursor: "pointer",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Connect
                    </button>
                  </div>
                ),
                { autoClose: 8000 },
              );
            } else {
              toast.warn("Connect Google Calendar from Profile to enable reminders.");
            }
          },
        );
        if (result?.success) {
          if (typeof onTodoCalendarChange === "function" && result.eventId) {
            onTodoCalendarChange(created._id, result.eventId);
          }
          toast.success(`📅 Reminder added to Google Calendar at ${capturedReminderTime}`);
        }
      } catch {
        toast.error("Could not create calendar event. Try again.");
      }
    }
  };

  const handleAddToCalendar = async (todo, reminderMinutes) => {
    const selectedDate = calendarForm.date || todayStr;
    const selectedTime = calendarForm.time || "14:00";

    if (!/^\d{2}:\d{2}$/.test(selectedTime)) {
      toast.warn("Please pick a valid time.");
      return;
    }

    if (isReminderInPast(selectedDate, selectedTime)) {
      toast.warn("⚠️ That time is already past — Google won't notify for past events. Pick a future time.");
      return;
    }

    setShowPickerForTodo(null);
    setCalendarStatuses((prev) => ({ ...prev, [todo._id]: "adding" }));

    try {
      const eventDate = new Date(`${selectedDate}T${selectedTime}:00`);

      if (Number.isNaN(eventDate.getTime())) {
        toast.warn("Please pick a valid date and time.");
        setCalendarStatuses((prev) => ({ ...prev, [todo._id]: null }));
        return;
      }

      const result = await calendarService.smartAddToCalendar(
        {
          title: `✓ ${todo.text}`,
          description: `Quick todo from Task Tracker Pro\nDate: ${new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}\nTime: ${selectedTime}`,
          date: selectedDate,
          startTime: selectedTime,
          durationMinutes: 30,
          reminderMinutes,
          todoId: todo._id,
        },
        () => {
          // Not connected — show in-app toast with Connect button, no redirect
          setCalendarStatuses((prev) => ({ ...prev, [todo._id]: "error" }));
          setTimeout(
            () =>
              setCalendarStatuses((prev) => ({ ...prev, [todo._id]: null })),
            3000,
          );
          if (onConnectCalendar) {
            toast.info(
              ({ closeToast }) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span>
                    Connect Google Calendar to enable background reminders
                  </span>
                  <button
                    onClick={() => {
                      closeToast();
                      onConnectCalendar();
                    }}
                    style={{
                      background: "linear-gradient(135deg,#1e293b,#334155)",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: 6,
                      padding: "4px 12px",
                      cursor: "pointer",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Connect
                  </button>
                </div>
              ),
              { autoClose: 8000 },
            );
          } else {
            toast.warn(
              "Connect Google Calendar from the Profile menu to enable background reminders.",
            );
          }
        },
      );

      if (result.success) {
        if (typeof onTodoCalendarChange === "function" && result.eventId) {
          onTodoCalendarChange(todo._id, result.eventId);
        }
        setCalendarStatuses((prev) => ({ ...prev, [todo._id]: "added" }));
        setTimeout(
          () => setCalendarStatuses((prev) => ({ ...prev, [todo._id]: null })),
          2000,
        );
      } else {
        setCalendarStatuses((prev) => ({ ...prev, [todo._id]: null }));
      }
    } catch (err) {
      console.error("Calendar error:", err);
      setCalendarStatuses((prev) => ({ ...prev, [todo._id]: "error" }));
      setTimeout(
        () => setCalendarStatuses((prev) => ({ ...prev, [todo._id]: null })),
        2000,
      );
    }
  };

  const handleRemoveFromCalendar = async (todo) => {
    if (!todo.calendarEventId) return;
    setShowPickerForTodo(null);
    setCalendarStatuses((prev) => ({ ...prev, [todo._id]: "adding" }));
    try {
      await calendarService.deleteEvent(todo.calendarEventId);
      if (typeof onTodoCalendarChange === "function") onTodoCalendarChange(todo._id, null);
      else todo.calendarEventId = null;
      setCalendarStatuses((prev) => ({ ...prev, [todo._id]: null }));
      toast.success("🗑 Removed from Google Calendar");
    } catch (err) {
      console.error("Calendar delete error:", err);
      setCalendarStatuses((prev) => ({ ...prev, [todo._id]: null }));
      toast.error("Failed to remove calendar event");
    }
  };

  return (
    <div className="todo-list-container">
      <div className="todo-header">
        <h3>✓ Quick Todos</h3>
        <p className="todo-subtitle">Simple checklist for daily tasks</p>
      </div>

      <TodoAssistant
        onCreateTodo={async ({ text, deadline }) => {
          await onAddTodo(text, deadline || todayStr);
        }}
      />

      <form onSubmit={handleSubmit} className="todo-input-form">
        <div className="todo-input-group">
          <input
            type="text"
            value={newTodo}
            onChange={(e) => setNewTodo(e.target.value)}
            placeholder="Add a quick todo..."
            className="todo-input"
          />
          <input
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className="todo-deadline-input"
            title="Optional deadline"
          />
          <div className="todo-reminder-btn-wrap">
            <button
              type="button"
              className={`todo-calendar-btn${reminderMinutes > 0 ? " calendar-added" : ""}`}
              title={reminderMinutes > 0 ? `Reminder set: ${newTodoCalForm.time} • ${reminderMinutes < 60 ? reminderMinutes + " min" : reminderMinutes === 1440 ? "1 day" : reminderMinutes / 60 + " hr"} before` : "Set date, time & reminder"}
              onClick={() => {
                setNewTodoCalForm({ date: deadline || todayStr, time: "09:00", reminderMinutes: reminderMinutes || 0 });
                setShowNewTodoCal(true);
              }}
            >
              📅
            </button>
          </div>
          {showNewTodoCal && ReactDOM.createPortal(
            <div className="calendar-picker-overlay" onClick={() => setShowNewTodoCal(false)}>
              <div className="calendar-reminder-picker" onClick={(e) => e.stopPropagation()}>
                <div className="calendar-picker-title">Set Date, Time &amp; Reminder</div>
                <div className="calendar-picker-context">
                  <div className="calendar-picker-context-label">Adding reminder for</div>
                  <div className="calendar-picker-context-title">{newTodo || "New todo"}</div>
                  <div className="calendar-picker-context-meta">
                    {new Date((newTodoCalForm.date || todayStr) + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                  </div>
                </div>
                <div className="calendar-picker-grid">
                  <label className="calendar-picker-field">
                    <span>Date</span>
                    <input
                      type="date"
                      value={newTodoCalForm.date}
                      onChange={(e) => setNewTodoCalForm((p) => ({ ...p, date: e.target.value || todayStr }))}
                    />
                  </label>
                  <label className="calendar-picker-field">
                    <span>Time</span>
                    <input
                      type="time"
                      value={newTodoCalForm.time}
                      onChange={(e) => setNewTodoCalForm((p) => ({ ...p, time: e.target.value }))}
                    />
                  </label>
                </div>
                <label className="calendar-picker-field">
                  <span>Reminder</span>
                  <select
                    value={newTodoCalForm.reminderMinutes}
                    onChange={(e) => setNewTodoCalForm((p) => ({ ...p, reminderMinutes: Number(e.target.value) }))}
                  >
                    <option value={0}>No reminder</option>
                    <option value={5}>5 min before</option>
                    <option value={10}>10 min before</option>
                    <option value={15}>15 min before</option>
                    <option value={30}>30 min before</option>
                    <option value={60}>1 hour before</option>
                  </select>
                </label>
                <div className="calendar-picker-actions">
                  <button className="btn-cancel" type="button" onClick={() => setShowNewTodoCal(false)}>Cancel</button>
                  <button
                    className="btn-add"
                    type="button"
                    onClick={() => {
                      setDeadline(newTodoCalForm.date);
                      setReminderMinutes(newTodoCalForm.reminderMinutes);
                      setReminderTime(newTodoCalForm.time);
                      setShowNewTodoCal(false);
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
        <button type="submit" className="todo-add-btn" disabled={isAddingTodo}>
          {isAddingTodo ? "Adding..." : "+ Add"}
        </button>
      </form>

      <div className="todo-items">
        {todos.length === 0 ? (
          <div className="todo-empty">
            <span>📝</span>
            <p>No todos yet. Add one above!</p>
          </div>
        ) : (
          <>
          {activeTodos.length === 0 && missedTodos.length === 0 && completedTodos.length > 0 ? (
            <div className="todo-empty todo-all-done">
              <span>🎉</span>
              <p>All done for today!</p>
            </div>
          ) : activeTodos.length === 0 && missedTodos.length > 0 ? (
            <div className="todo-empty todo-all-done">
              <span>👍</span>
              <p>Nothing to-do — missed items are parked below.</p>
            </div>
          ) : null}
          {activeTodos
            .map((todo) => {
              const todoTagDate = todo.deadline || todo.date || todayStr;
              const isTodayTag = todoTagDate === todayStr;
              const isOverdue =
                !todo.completed &&
                (todo.deadline
                  ? todo.deadline < todayStr
                  : todo.isOverdue);
              const isDragTarget = dragOverId === todo._id;
              return (
                <div
                  key={todo._id}
                  data-todoid={todo._id}
                  className={`todo-item ${todo.completed ? "completed" : ""} ${isTodayTag && !todo.completed ? "today-item" : ""} ${isOverdue ? "overdue-item" : ""} ${todo.missed ? "missed-item" : ""} ${isDragTarget && dragAbove ? "drag-over-above" : ""} ${isDragTarget && !dragAbove ? "drag-over-below" : ""}`}
                  draggable={!todo.completed}
                  onDragStart={(e) => handleDragStart(e, todo._id)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, todo._id)}
                  onDrop={(e) => handleDrop(e, todo._id)}
                >
                  {!todo.completed && (
                    <span className="todo-drag-handle" title="Drag to reorder">⠿</span>
                  )}
                  <input
                    type="checkbox"
                    checked={todo.completed}
                    onChange={() => onToggleTodo(todo._id)}
                    className="todo-checkbox"
                    disabled={togglingTodo[todo._id]}
                  />
                  <span className="todo-text">
                    {isOverdue && <span className="overdue-tag">OVERDUE</span>}
                    {todo.missed && !todo.completed && <span className="missed-todo-tag">MISSED</span>}
                    {editingId === todo._id ? (
                      <input
                        ref={editInputRef}
                        className="todo-inline-edit"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        onBlur={() => handleEditSave(todo)}
                        onKeyDown={(e) => handleEditKeyDown(e, todo)}
                        maxLength={200}
                      />
                    ) : (
                      <>
                        {todo.text}
                        {todoTagDate && (
                          <span
                            className={`todo-deadline-badge ${isTodayTag ? "deadline-today" : ""} ${isOverdue ? "deadline-overdue" : ""}`}
                            title={todo.deadline ? "Deadline" : "Todo day"}
                          >
                            {isTodayTag
                              ? "Today"
                              : new Date(
                                  todoTagDate + "T00:00:00",
                                ).toLocaleDateString("en-US", {
                                  weekday: "short",
                                })}{" "}
                            •{" "}
                            {new Date(todoTagDate + "T00:00:00").toLocaleDateString(
                              "en-US",
                              { month: "short", day: "numeric" },
                            )}
                          </span>
                        )}
                      </>
                    )}
                  </span>
                  <div className="todo-actions">
                    <div className="calendar-btn-wrapper">
                      <button
                        onClick={() => handleCalBtnClick(todo)}
                        className={`todo-calendar-btn ${calendarStatuses[todo._id] === "added" || todo.calendarEventId ? "calendar-added" : ""}`}
                        title={
                          todo.calendarEventId
                            ? "In Google Calendar — click to manage"
                            : todoTagDate
                              ? `Add to calendar on ${new Date(todoTagDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                              : calendarStatuses[todo._id] === "added"
                                ? "Added to Calendar!"
                                : "Add to Google Calendar"
                        }
                        disabled={calendarStatuses[todo._id] === "adding"}
                      >
                        {calendarStatuses[todo._id] === "adding"
                          ? "⏳"
                          : calendarStatuses[todo._id] === "added" || todo.calendarEventId
                            ? "✅"
                            : "📅"}
                      </button>
                      {showPickerForTodo === todo._id &&
                        ReactDOM.createPortal(
                          <div
                            className="calendar-picker-overlay"
                            onClick={() => setShowPickerForTodo(null)}
                          >
                            <div
                              className="calendar-reminder-picker"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="calendar-picker-title">
                                Set Date, Time & Reminder
                              </div>
                              <div className="calendar-picker-context">
                                <div className="calendar-picker-context-label">
                                  Adding reminder for
                                </div>
                                <div className="calendar-picker-context-title">
                                  {todo.text}
                                </div>
                                <div className="calendar-picker-context-meta">
                                  {new Date(
                                    todoTagDate + "T00:00:00",
                                  ).toLocaleDateString("en-US", {
                                    weekday: "short",
                                    month: "short",
                                    day: "numeric",
                                  })}
                                </div>
                              </div>
                              {todo.calendarEventId ? (
                                <>
                                  <div className="calendar-event-status">✅ In Google Calendar</div>
                                  <div className="calendar-picker-actions">
                                    <button
                                      className="btn-cancel"
                                      onClick={() => setShowPickerForTodo(null)}
                                      type="button"
                                    >
                                      Close
                                    </button>
                                    <button
                                      className="btn-add btn-remove-calendar"
                                      onClick={() => handleRemoveFromCalendar(todo)}
                                      type="button"
                                      style={{ background: 'var(--danger, #ba1a1a)' }}
                                    >
                                      🗑 Remove
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <>
                              <div className="calendar-picker-grid">
                                <label className="calendar-picker-field">
                                  <span>Date</span>
                                  <input
                                    type="date"
                                    value={calendarForm.date}
                                    onChange={(e) =>
                                      setCalendarForm((prev) => ({
                                        ...prev,
                                        date: e.target.value || todayStr,
                                      }))
                                    }
                                  />
                                </label>
                                <label className="calendar-picker-field">
                                  <span>Time</span>
                                  <input
                                    type="time"
                                    value={calendarForm.time}
                                    onChange={(e) =>
                                      setCalendarForm((prev) => ({
                                        ...prev,
                                        time: e.target.value,
                                      }))
                                    }
                                  />
                                </label>
                              </div>
                              <label className="calendar-picker-field">
                                <span>Reminder</span>
                                <select
                                  value={calendarForm.reminderMinutes}
                                  onChange={(e) =>
                                    setCalendarForm((prev) => ({
                                      ...prev,
                                      reminderMinutes: Number(e.target.value),
                                    }))
                                  }
                                >
                                  <option value={0}>No reminder</option>
                                  <option value={5}>5 min before</option>
                                  <option value={10}>10 min before</option>
                                  <option value={15}>15 min before</option>
                                  <option value={30}>30 min before</option>
                                  <option value={60}>1 hour before</option>
                                </select>
                              </label>
                              <div className="calendar-picker-actions">
                                <button
                                  className="btn-cancel"
                                  onClick={() => setShowPickerForTodo(null)}
                                  type="button"
                                >
                                  Cancel
                                </button>
                                <button
                                  className="btn-add"
                                  onClick={() =>
                                    handleAddToCalendar(
                                      todo,
                                      calendarForm.reminderMinutes,
                                    )
                                  }
                                  type="button"
                                >
                                  Add
                                </button>
                              </div>
                                </>
                              )}
                            </div>
                          </div>,
                          document.body,
                        )}
                    </div>
                    {/* Edit button */}
                    {!todo.completed && editingId !== todo._id && (
                      <button
                        onClick={() => handleEditStart(todo)}
                        className="todo-edit-btn"
                        title="Edit todo text"
                      >
                        ✎
                      </button>
                    )}
                    {/* Mark Missed Button — opens re-remind prompt */}
                    {!todo.completed && (
                      <button
                        onClick={() => handleMissedClick(todo)}
                        className={`todo-missed-btn ${todo.missed ? "active-missed" : ""}`}
                        title={todo.missed ? "Remove missed mark" : "Mark as missed"}
                      >
                        {todo.missed ? "↺" : "✗"}
                      </button>
                    )}
                    <button
                      onClick={() => onDeleteTodo(todo._id)}
                      className="todo-delete-btn"
                      title="Delete todo"
                      disabled={deletingTodo[todo._id]}
                    >
                      {deletingTodo[todo._id] ? "..." : "×"}
                    </button>
                  </div>
                </div>
              );
            })}

          {/* Missed tray — sunk to the bottom, collapsed by default so a pile
              of missed items never overshadows the need-to-do list above. */}
          {missedTodos.length > 0 && (
            <div className="todo-missed-section">
              <button
                type="button"
                className="todo-missed-toggle"
                onClick={() => setShowMissed((v) => !v)}
                title={showMissed ? "Collapse missed" : "Expand missed"}
              >
                <span className="todo-missed-toggle-caret">{showMissed ? "▾" : "▸"}</span>
                <span className="missed-todo-tag">MISSED</span>
                <span className="todo-missed-count">
                  {missedTodos.length} parked below — {showMissed ? "hide" : "review"}
                </span>
              </button>
              {showMissed ? (
                <div className="todo-missed-list">
                  {missedTodos.map((todo) => (
                    <div key={todo._id} className="todo-item missed-item missed-row">
                      <input
                        type="checkbox"
                        checked={false}
                        onChange={() => onToggleTodo(todo._id)}
                        className="todo-checkbox"
                        disabled={togglingTodo[todo._id]}
                        title="Mark done"
                      />
                      <span className="todo-text">
                        <span className="missed-todo-tag">MISSED</span>
                        {todo.text}
                        {(todo.deadline || todo.date) && (
                          <span className="todo-deadline-badge">
                            {(todo.deadline || todo.date) === todayStr
                              ? "Today"
                              : new Date((todo.deadline || todo.date) + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          </span>
                        )}
                      </span>
                      <div className="todo-actions" style={{ opacity: 1, transform: "none" }}>
                        <button
                          className="todo-remind-btn"
                          title="Re-remind tomorrow"
                          onClick={() => { setRemindDate(getTomorrowStr()); setMissedPrompt(todo); }}
                        >
                          🔔
                        </button>
                        <button
                          className="todo-missed-btn active-missed"
                          title="Undo missed — back to to-do"
                          onClick={() => onMarkTodoMissed && onMarkTodoMissed(todo._id, false)}
                        >
                          ↺
                        </button>
                        <button
                          onClick={() => onDeleteTodo(todo._id)}
                          className="todo-delete-btn"
                          title="Remove missed"
                          disabled={deletingTodo[todo._id]}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="todo-missed-footer">
                    <span>Missed items stay here until you re-remind or clear them.</span>
                    <button
                      type="button"
                      className="todo-clear-btn"
                      onClick={() => onClearMissed && onClearMissed()}
                    >
                      Clear missed ({missedTodos.length})
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Completed — always last */}
          {completedTodos.map((todo) => {
            const todoTagDate = todo.deadline || todo.date || todayStr;
            return (
              <div key={todo._id} className="todo-item completed">
                <input
                  type="checkbox"
                  checked={todo.completed}
                  onChange={() => onToggleTodo(todo._id)}
                  className="todo-checkbox"
                  disabled={togglingTodo[todo._id]}
                />
                <span className="todo-text">
                  {todo.text}
                  {todoTagDate && (
                    <span className="todo-deadline-badge">
                      {new Date(todoTagDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  )}
                </span>
                <div className="todo-actions" style={{ opacity: 1, transform: "none" }}>
                  <button
                    onClick={() => onDeleteTodo(todo._id)}
                    className="todo-delete-btn"
                    title="Delete todo"
                    disabled={deletingTodo[todo._id]}
                  >
                    {deletingTodo[todo._id] ? "..." : "×"}
                  </button>
                </div>
              </div>
            );
          })}
          </>
        )}
      </div>

      {/* Re-remind prompt — shown right after pressing Missed.
          Pick a date to bring it back, or dismiss to leave it parked below.
          Dismissing keeps the top list clean; Remove deletes it. */}
      {missedPrompt && ReactDOM.createPortal(
        <div className="calendar-picker-overlay" onClick={handleMissedDismiss}>
          <div className="calendar-reminder-picker missed-prompt" onClick={(e) => e.stopPropagation()}>
            <div className="calendar-picker-title">Marked as missed</div>
            <div className="calendar-picker-context">
              <div className="calendar-picker-context-label">You missed</div>
              <div className="calendar-picker-context-title">{missedPrompt.text}</div>
              <div className="calendar-picker-context-meta">
                Want a re-reminder? Pick a day to bring it back to your to-do list.
                If not, it stays parked in the Missed tray below (out of the way).
              </div>
            </div>
            <label className="calendar-picker-field">
              <span>Remind on</span>
              <input
                type="date"
                value={remindDate}
                min={todayStr}
                onChange={(e) => setRemindDate(e.target.value)}
              />
            </label>
            <div className="missed-prompt-actions">
              <button type="button" className="btn-add" onClick={() => handleRemindConfirm("tomorrow")}>
                🔔 Tomorrow
              </button>
              <button type="button" className="btn-add" onClick={() => handleRemindConfirm("custom")}>
                📅 {remindDate || "Pick date"}
              </button>
            </div>
            <div className="calendar-picker-actions">
              <button type="button" className="btn-cancel" onClick={handleMissedDismiss}>
                No, keep parked
              </button>
              <button type="button" className="btn-add btn-remove-calendar" onClick={handleMissedRemove}>
                🗑 Remove
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {todos.length > 0 && (
        <div className="todo-stats">
          <span>{todos.filter((t) => t.completed).length} of {todos.length} completed</span>
          <div className="todo-stats-actions">
            {onClearCompleted && todos.some((t) => t.completed) && (
              <button
                className="todo-clear-btn"
                onClick={onClearCompleted}
                title="Clear all completed todos"
              >
                ✓ Clear Completed
              </button>
            )}
            {onDeleteAll && (
              <button
                className="todo-clear-btn todo-delete-all-btn"
                onClick={onDeleteAll}
                title="Delete all todos"
              >
                🗑 Delete All
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TodoList;
